import { Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text, Image } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { getConfigFromStorage } from '@/utils/config';
import { useTranslation } from 'react-i18next';
import { CustomTable } from '@/components/CustomTable/CustomTable';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { StoreMainBanner } from '@/model';
import { formatDateToDateTimeDisplay } from '@/utils/formatDateToDateTimeDisplay';

export interface StoreMainBannersTableProps {
  data: StoreMainBanner[];
  isLoading: boolean;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  sorting?: { field: string; desc: boolean };
  onSortingChange?: (field: string, desc: boolean) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onViewClick: (item: StoreMainBanner) => void;
  onDeleteClick: (item: StoreMainBanner) => void;
}

export const StoreMainBannersTable = ({
  data,
  isLoading,
  pageNumber,
  pageSize,
  totalPages,
  totalItems,
  sorting,
  onSortingChange,
  onPaginationChange,
  onViewClick,
  onDeleteClick,
}: StoreMainBannersTableProps) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<StoreMainBanner>();
  const isMultiTeam = getConfigFromStorage()?.is_multi_team ?? true;

  const columns = [
    // Data de criação
    columnHelper.accessor('created_at', {
      cell: props => formatDateToDateTimeDisplay(props.getValue()),
      header: t('pages.application_store.components.store_main_banners_table.banner_table_date'),
    }),

    // Time (escudo + nome)
    columnHelper.accessor('team_name', {
      cell: props => {
        const { team_image, team_name } = props.row.original;
        return team_name ? (
          <Flex alignItems='center' gap={2}>
            {team_image ? <Image src={team_image} alt={team_name} w='24px' h='24px' objectFit='contain' /> : null}
            <Text fontWeight='bold'>{team_name}</Text>
          </Flex>
        ) : (
          <Text>-</Text>
        );
      },
      header: t('pages.application_store.components.store_main_banners_table.banner_table_team'),
    }),

    // Título do banner
    columnHelper.accessor('title', {
      cell: props => (
        <Text fontWeight='medium' noOfLines={1}>
          {props.getValue()}
        </Text>
      ),
      header: t('pages.application_store.components.store_main_banners_table.banner_table_title'),
    }),

    // Ações (exibir + deletar)
    columnHelper.display({
      id: 'actions',
      header: t('pages.application_store.components.store_main_banners_table.banner_table_actions'),
      cell: props => (
        <Flex justifyContent='end'>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_store.components.store_main_banners_table.banner_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onViewClick?.(props.row.original)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_store.components.store_main_banners_table.banner_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick?.(props.row.original)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_store.components.store_main_banners_table.banner_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      ),
      enableSorting: false,
      meta: {
        align: 'right',
      },
    }),
  ];

  return (
    <CustomTable
      data={data}
      isLoading={isLoading}
      pageNumber={pageNumber}
      pageSize={pageSize}
      totalPages={totalPages}
      totalItems={totalItems}
      columns={isMultiTeam ? columns : columns.filter(col => (col as any).accessorKey !== 'team_name')}
      onPaginationChange={onPaginationChange}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
